import { Stack, Text, Box, Group, SimpleGrid, Skeleton, Alert } from '@mantine/core'
import { IconExternalLink, IconNetwork, IconKey } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { securityApi } from '@/api/security'
import { formatApiError } from '@/api/errors'
import { WidgetCard } from '@/components/ui'
import { PriorityQueue } from '../Posture/PriorityQueue'
import type { SecurityFinding } from '@/api/security'

// A lente Pentest: três check_id re-apresentados sob esta aba (ver PENTEST_CHECK_IDS
// no backend). A exposição externa (A01-003) também aparece na Postura — aqui é
// re-apresentada sob a lente de exposição, não duplicada.
const LENTE = { ext: 'A01-003', int: 'A01-006', iam: 'A01-007' } as const

interface PentestPanelProps {
  tenantId: number
  onSelect?: (finding: SecurityFinding) => void
}

export function PentestPanel({ tenantId, onSelect }: PentestPanelProps) {
  const overview = useQuery({
    queryKey: ['security', 'pentest', 'overview', tenantId],
    queryFn: () => securityApi.getPentestOverview(tenantId),
    enabled: !!tenantId,
  })
  // A lente é por check_id; a API filtra por categoria (A01 cobre os três),
  // então trazemos os A01 abertos e separamos por check_id no cliente.
  const findings = useQuery({
    queryKey: ['security', 'pentest', 'findings', tenantId],
    queryFn: () => securityApi.listFindings(tenantId, { category: 'A01', aberto: true, limit: 200 }),
    enabled: !!tenantId,
  })

  if (overview.isLoading || findings.isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={64} />
        <Skeleton height={180} />
        <Skeleton height={180} />
      </Stack>
    )
  }
  if (overview.isError) {
    return <Alert color="red">{formatApiError(overview.error, 'Falha ao carregar o pentest')}</Alert>
  }

  const ov = overview.data
  const todos = findings.data?.findings ?? []
  const porCheck = (cid: string) => todos.filter((f) => f.check_id === cid)
  const ext = porCheck(LENTE.ext)
  const inter = porCheck(LENTE.int)
  const iam = porCheck(LENTE.iam)
  const ultima = ov?.ultima_varredura?.completed_at

  return (
    <Stack gap="lg">
      <WidgetCard>
        <Group gap={40} wrap="wrap">
          <Resumo label="Em aberto" valor={ov?.em_aberto_total ?? 0} />
          <Resumo label="Críticos" valor={ov?.em_aberto?.critical ?? 0} cor="#b3261e" />
          <Resumo label="Exceções aceitas" valor={ov?.excecao_aceita ?? 0} cor="#d9822b" />
          <Resumo label="Corrigidos (reteste)" valor={ov?.corrigido ?? 0} cor="#098d93" />
          <Box style={{ marginLeft: 'auto' }}>
            <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
              Última varredura
            </Text>
            <Text size="sm" ff="var(--mantine-font-family-monospace)">
              {ultima ? new Date(ultima).toLocaleString('pt-BR') : '—'}
            </Text>
          </Box>
        </Group>
      </WidgetCard>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Secao titulo="Exposição externa — Security Groups" icon={<IconExternalLink size={17} />} n={ext.length}>
          <PriorityQueue findings={ext} onSelect={onSelect} />
        </Secao>
        <Secao titulo="Alcance interno — SG entre recursos" icon={<IconNetwork size={17} />} n={inter.length}>
          <PriorityQueue findings={inter} onSelect={onSelect} />
        </Secao>
      </SimpleGrid>

      <Secao titulo="Escalada de privilégio — IAM" icon={<IconKey size={17} />} n={iam.length}>
        <PriorityQueue findings={iam} onSelect={onSelect} />
      </Secao>
    </Stack>
  )
}

function Resumo({ label, valor, cor }: { label: string; valor: number; cor?: string }) {
  return (
    <Box>
      <Text fw={700} style={{ fontSize: 26, lineHeight: 1, color: cor }}>
        {valor}
      </Text>
      <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
        {label}
      </Text>
    </Box>
  )
}

function Secao({ titulo, icon, n, children }: { titulo: string; icon: React.ReactNode; n: number; children: React.ReactNode }) {
  return (
    <WidgetCard>
      <Group justify="space-between" mb="sm">
        <Group gap={8}>
          <Box c="#025468" style={{ display: 'flex' }}>{icon}</Box>
          <Text size="sm" fw={600} c="#025468">{titulo}</Text>
        </Group>
        <Text size="xs" c="dimmed" ff="var(--mantine-font-family-monospace)">{n} em aberto</Text>
      </Group>
      {children}
    </WidgetCard>
  )
}
